import React from 'react';
import { Wallet, Eye, EyeOff, ChevronRight, CreditCard, Receipt, FileText } from 'lucide-react';
import { APP_VERSION } from '@/version';
import DomainActasCard from './DomainActasCard';
import { formatDiscountExpiry } from './dateHelpers';

const DesktopLayout = ({
  debtor, tenant, portfolio, primaryColor, documentInfo, visibleFields,
  totalDebtWithFees, discountAmount, feesAmount, amountToPay,
  nearestMontoExpiry, nearestDiscountExpiry,
  allowPartialActaPayment, hasActaAmounts, pendingActas, canSelectActas,
  showBalance, setShowBalance, showDetail, setShowDetail,
  selectedActas, toggleActa,
  quickActions, onPayNow, onShowDocument
}) => {
  return (
    <div className="hidden lg:flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-80 bg-white border-r border-slate-200 flex flex-col min-h-screen sticky top-0">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            {(portfolio?.logo_url || tenant?.logo_url) ? (
              <img src={portfolio?.logo_url || tenant?.logo_url} alt={tenant?.name} className="w-12 h-12 rounded-xl object-contain bg-slate-50 p-1 border border-slate-200" />
            ) : (
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                <Wallet className="w-6 h-6" style={{ color: primaryColor }} />
              </div>
            )}
            <div>
              <p className="text-xs text-slate-500">Hola,</p>
              <p className="font-semibold text-slate-900 text-lg">{debtor?.name?.split(' ')[0]}</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Tus Datos</h3>
          <div className="space-y-2">
            {visibleFields.filter(f => !['total_debt', 'debt_date', 'plate'].includes(f.field_id)).map(field => {
              const value = debtor?.[field.field_id] || debtor?.custom_fields?.[field.field_id];
              if (!value) return null;
              return (
                <div key={field.field_id} className="bg-slate-50 rounded-lg px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{field.field_name}</p>
                  <p className="font-medium text-slate-800 text-sm">{value}</p>
                </div>
              );
            })}
            {debtor?.domains?.filter(d => d.plate).map((d, i) => (
              <div key={i} className="bg-slate-50 rounded-lg px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Dominio {debtor.domains.filter(dd => dd.plate).length > 1 ? i + 1 : ''}</p>
                <p className="font-mono font-medium text-slate-800 text-sm">{d.plate}</p>
              </div>
            ))}
          </div>

          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3 mt-6">Acciones</h3>
          <div className="space-y-1">
            {quickActions.map(action => (
              <button key={action.id} onClick={action.onClick} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${action.color}15` }}>
                  <action.icon className="w-4 h-4" style={{ color: action.color }} />
                </div>
                <span className="text-sm text-slate-700">{action.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-slate-100">
          <button onClick={onPayNow} className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 transition-opacity hover:opacity-90" style={{ backgroundColor: primaryColor }} data-testid="desktop-pay-btn">
            <CreditCard className="w-5 h-5" /> Pagar Ahora
          </button>
          <p className="text-[10px] text-slate-400 text-center mt-2" data-testid="app-version">v{APP_VERSION}</p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto p-8">
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-500 text-sm font-medium">Deuda Total</span>
          </div>
          <div className="flex items-center gap-4 mb-2">
            <div className="text-5xl font-bold text-slate-900" data-testid="desktop-total-debt">
              {showBalance ? (<><span className="text-3xl">$</span>{totalDebtWithFees.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</>) : '$ ------'}
            </div>
            <button onClick={() => setShowBalance(!showBalance)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
              {showBalance ? <Eye className="w-5 h-5 text-slate-400" /> : <EyeOff className="w-5 h-5 text-slate-400" />}
            </button>
          </div>
          {nearestMontoExpiry && <p className="text-xs font-semibold text-amber-700 mb-4">Vence el {formatDiscountExpiry(nearestMontoExpiry)}</p>}

          {discountAmount != null && discountAmount > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-4">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-sm text-emerald-700 font-medium">Monto con Quita</span>
                  <p className="text-xs text-emerald-600 mt-0.5">Pagando hoy, tu deuda queda en:</p>
                  {nearestDiscountExpiry && <p className="text-xs font-semibold text-amber-700 mt-1">Vence el {formatDiscountExpiry(nearestDiscountExpiry)}</p>}
                </div>
                <span className="text-2xl font-bold text-emerald-700">${discountAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}

          <button onClick={() => setShowDetail(!showDetail)} className="mt-5 w-full text-sm font-medium flex items-center justify-center gap-1 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors" style={{ color: primaryColor }} data-testid="desktop-ver-detalle">
            {showDetail ? 'Ocultar detalle' : 'Ver detalle'} <ChevronRight className={`w-4 h-4 transition-transform ${showDetail ? 'rotate-90' : ''}`} />
          </button>

          {showDetail && (
            <div className="border-t border-slate-100 pt-6 mt-6 space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                {debtor?.dni && <div><span className="text-slate-500 text-xs">DNI</span><p className="font-medium text-slate-800">{debtor.dni}</p></div>}
                {debtor?.cuit && <div><span className="text-slate-500 text-xs">CUIT</span><p className="font-medium text-slate-800">{debtor.cuit}</p></div>}
                {debtor?.client_code && <div><span className="text-slate-500 text-xs">Codigo</span><p className="font-medium text-slate-800">{debtor.client_code}</p></div>}
                {debtor?.email && <div><span className="text-slate-500 text-xs">Email</span><p className="font-medium text-slate-800">{debtor.email}</p></div>}
                {debtor?.phone && <div><span className="text-slate-500 text-xs">Telefono</span><p className="font-medium text-slate-800">{debtor.phone}</p></div>}
                {feesAmount > 0 && <div><span className="text-slate-500 text-xs">Honorarios</span><p className="font-medium text-amber-700">${feesAmount.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p></div>}
              </div>
              {debtor?.domains?.length > 0 && debtor.domains.some(d => d.plate) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-amber-800">Dominios e Infracciones</h4>
                    {allowPartialActaPayment && hasActaAmounts && pendingActas.length > 0 && (
                      <button onClick={() => toggleActa('__all__')} className="text-xs font-medium text-amber-700 hover:underline">
                        {selectedActas.length === pendingActas.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {debtor.domains.filter(d => d.plate).map((domain, di) => (
                      <DomainActasCard key={di} domain={domain} validActas={domain.actas || []} allowPartialPayment={allowPartialActaPayment && hasActaAmounts} selectedActas={selectedActas} onToggleActa={toggleActa} />
                    ))}
                  </div>
                  {canSelectActas && selectedActas.length > 0 && (
                    <div className="mt-4 bg-white border border-amber-300 rounded-xl p-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-amber-800">{selectedActas.length} acta{selectedActas.length > 1 ? 's' : ''} seleccionada{selectedActas.length > 1 ? 's' : ''}</p>
                        <p className="text-lg font-bold text-amber-900">Total: ${amountToPay.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <button onClick={onPayNow} className="px-6 py-2.5 rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: primaryColor }}>Pagar seleccion</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {documentInfo && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6 flex items-center justify-between cursor-pointer hover:border-slate-300 transition-colors" onClick={onShowDocument}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center"><Receipt className="w-6 h-6 text-slate-600" /></div>
              <div>
                <p className="font-semibold text-slate-900">{documentInfo.document_name || 'Documento Adjunto'}</p>
                <p className="text-sm text-slate-500">{portfolio?.name}</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        )}
      </main>
    </div>
  );
};

export default DesktopLayout;
